/**
 * RouteErrorView — 라우터 loader 가 실패했을 때 outlet 에 마운트되는 fallback 페이지.
 *
 * 진입:
 *   SolaRouter 의 errorView 옵션이 이 view 를 생성. main.ts 참조.
 *
 * 책임:
 *   1) 에러 종류 분류 (404 / 5xx / 네트워크 등)
 *   2) 사람 친화적인 메시지 + 다음 액션 (목록으로, 다시 시도)
 *
 * 일부러 sidebar 안에 들어가도록 outlet 만 차지 — AppShell 의 사이드바·헤더는
 * 그대로 유지되어 사용자가 다른 페이지로 쉽게 이동 가능.
 *
 * NotFoundView (URL 매칭 실패) 와 RouteErrorView (loader 실패) 는 책임이 다름.
 * 전자는 "그런 라우트가 없다", 후자는 "그 라우트의 데이터가 없거나 에러".
 */

import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';
import { escapeHtml } from '../lib/cells';

interface RouteErrorData {
  path:    string;
  message: string;
  kind:    'not_found' | 'server' | 'network' | 'unknown';
}

/** 에러 객체를 사용자가 이해할 수 있는 종류로 분류. */
function classify(err: unknown): { kind: RouteErrorData['kind']; message: string } {
  const raw = err instanceof Error ? err.message : String(err);

  // api/client.ts 의 getJson 은 "HTTP 404 ..." 형태로 throw — 그걸 보고 판단
  if (/HTTP\s*404/i.test(raw))            return { kind: 'not_found', message: raw };
  if (/HTTP\s*5\d\d/.test(raw))            return { kind: 'server',    message: raw };
  if (/network|fetch|failed to fetch/i.test(raw))
                                           return { kind: 'network',   message: raw };
  return { kind: 'unknown', message: raw };
}

export class RouteErrorView extends SolaView<RouteErrorData> {
  constructor(params: { error: unknown; path: string }) {
    super();
    const { kind, message } = classify(params.error);
    this.data = {
      path:    params.path,
      kind,
      message,
    };
  }

  override getTitle(): string {
    switch (this.data.kind) {
      case 'not_found': return '페이지 없음 — HCLTech';
      case 'server':    return '서버 오류 — HCLTech';
      case 'network':   return '네트워크 오류 — HCLTech';
      default:          return '오류 — HCLTech';
    }
  }

  render(): HTMLElement {
    const { kind, path, message } = this.data;

    const title = {
      not_found: '요청하신 페이지를 찾을 수 없습니다',
      server:    '서버 오류가 발생했습니다',
      network:   '네트워크 연결을 확인해주세요',
      unknown:   '페이지를 불러올 수 없습니다',
    }[kind];

    const subtitle = {
      not_found: `URL: ${path}`,
      server:    '잠시 후 다시 시도해주세요. 문제가 계속되면 관리자에게 문의해주세요.',
      network:   '연결 상태를 확인하고 새로고침해주세요.',
      unknown:   '잠시 후 다시 시도해주세요.',
    }[kind];

    const code = kind === 'not_found' ? '404' : kind === 'server' ? '5XX' : '!';

    // 추정 — "딜" 경로면 딜 목록으로, 그 외엔 대시보드로
    const backHref = path.startsWith('/deal') ? '/deals'
                   : path.startsWith('/lead') ? '/leads'
                   : path.startsWith('/rfq')  ? '/rfq'
                   : '/';
    const backLabel = backHref === '/deals' ? '딜 목록으로'
                    : backHref === '/leads' ? '리드 목록으로'
                    : backHref === '/rfq'   ? 'RFQ 목록으로'
                    : '대시보드로';

    return htmlSolaWithInstance(this)`
      <div class="page page-route-error">
        <div class="route-error">
          <div class="route-error-code">${escapeHtml(code)}</div>
          <h1 class="route-error-title">${escapeHtml(title)}</h1>
          <p class="route-error-subtitle">${escapeHtml(subtitle)}</p>

          <div class="route-error-actions">
            <a class="btn-primary" data-link href="${escapeHtml(backHref)}">${escapeHtml(backLabel)}</a>
            <button class="btn-secondary" type="button" data-action="reload">다시 시도</button>
          </div>

          <details class="route-error-detail">
            <summary>기술 정보</summary>
            <pre>${escapeHtml(message)}</pre>
          </details>
        </div>
      </div>
    `;
  }

  override events = {
    click: {
      '@reload': 'handleReload',
    },
  };

  handleReload(): void {
    // 같은 URL 다시 로드 — loader 가 다시 호출되고 성공하면 정상 페이지로 swap
    window.location.reload();
  }
}
